/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, MouseEvent, type SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import validate from 'validate.js';
import { authAction, globalAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import * as messages from 'common/constants/messages/validations';
import {
  SEND_PASSWORD_RESET_EMAIL,
  USER_ACCESS_DENIED,
  USER_PROFILE_ERROR,
  USER_PROFILE_INFO_UPDATE,
} from 'common/constants/user';
import { userSchemaBase } from 'common/constants/userSchemaBase';
import { useStore } from 'common/store';
import { PageBreadcrumb, UseStoreValues } from 'common/types/store';
import { MessageBox } from 'components';
import { PixelCrop } from 'components/ImageContainer/utilities/types';
import ThemeModeControl from 'components/ThemeModeControl';
import ZoomControl from 'components/ZoomControl';
import { LayoutContext } from 'context';
import { WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import { useSendPasswordReset } from 'pages/Auth/hooks';
import { useFirmAssets, useImageUpload, useResponse, UsersHk } from 'services/hooks';
import { FirmAssetSignedUrlResponse } from 'services/hooks/useFirmAssets';
import { useGetUserDetails } from 'services/hooks/useUserSelected';
import ConnectedApps from './components/ConnectedApps';
import EmailsCheckbox from './components/EmailsCheckbox';
import { EmailNotificationOption } from './components/EmailsCheckbox.props';
import UserFormSkeleton from './components/UserFormSkeleton';
import { UserParams } from './types';
import { UserProfileFormState, UserProfileType } from './UserProfile.props';
import { userProfileClasses, UserProfileStyledGrid } from './UserProfile.styles';
import ImageContainer from '../../components/ImageContainer/ImageContainer';

const schema = userSchemaBase;

const UserProfile = ({ onSaveSuccess }: { onSaveSuccess?: (response: unknown) => void } = {}) => {
  const location = useLocation();
  const isWorkboard = location.pathname.includes(`/${WORKBOARD_SLUG}/`);
  const { userIdParam } = useParams() as Partial<UserParams>;
  const { useUpdateUserProfile } = UsersHk;
  const sendPasswordReset = useSendPasswordReset();
  const updateUserProfile = useUpdateUserProfile();
  const [{ user, firmId }, dispatch] = useStore() as unknown as UseStoreValues;

  const userId = Number(userIdParam ?? user?.id ?? 0);
  const { data: userSelected, refetch: refetchUserDetails } = useGetUserDetails(userId);
  const { setPageTitle, setPageActions, setPageBreadcrumbs } = useContext(LayoutContext);
  const [userInfo, setUserInfo] = useState<UserProfileType | null | string>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [originalImageSrc, setOriginalImageSrc] = useState('');
  const [saveButtonHasClicked, setSaveButtonHasClicked] = useState(false);
  const [imageHasBeenUploaded, setImageHasBeenUploaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formState, setFormState] = useState<UserProfileFormState>({
    isValid: false,
    values: {
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      profile: {
        id: '',
        picture_url: '',
        receive_feature_emails: false,
        receive_document_emails: false,
      },
    },
    touched: {
      username: false,
      first_name: false,
      last_name: false,
      email: false,
      profile: {
        id: false,
        picture_url: false,
        receive_feature_emails: false,
        receive_document_emails: false,
      },
    },
    errors: {
      username: [],
      first_name: [],
      last_name: [],
      email: [],
      profile: {
        id: [],
        picture_url: [],
        receive_feature_emails: [],
        receive_document_emails: [],
      },
    },
  });
  const latestPixelCrop = useRef({});
  const { successNotification, errorNotification } = useResponse();
  const { getFirmAssetSignedUrl, uploadImageFile } = useFirmAssets();
  const { getCroppedImage } = useImageUpload();
  const [{ isShowLoadingProgress }] = useStore() as unknown as UseStoreValues;

  const handleSetImageSrc = useCallback((value: SetStateAction<string | null>) => {
    setIsDirty(true);
    setImageSrc(currentValue => (typeof value === 'function' ? value(currentValue) ?? '' : value ?? ''));
  }, []);

  const handleResetPassword = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (formState.values.email) {
        const response = await sendPasswordReset(formState.values.email);
        if (isNull(response)) {
          // status code 204 => null body
          successNotification(messages.PASSWORD_CHANGE_SEND_MAIL);
        } else {
          errorNotification(messages.ERROR_OCCURRED);
        }
      }
    },
    [formState.values.email, sendPasswordReset, successNotification, errorNotification]
  );

  // Update ref for use in getCroppedImage
  useEffect(() => {
    if (croppedAreaPixels) {
      latestPixelCrop.current = croppedAreaPixels;
    }
  }, [croppedAreaPixels]);

  const saveUserProfileData = async () => {
    const userInfoValue = userInfo as UserProfileType;
    if (formState.isValid && userInfoValue?.id) {
      // Send all form values - API endpoint handles the structure
      const payload = { ...formState.values };
      const [response, error] = await updateUserProfile(userInfoValue?.id.toString(), payload);
      if (error) {
        if (error.status === 400) {
          errorNotification(messages.ERROR_OCCURRED);
          setFormState({
            ...formState,
            errors: JSON.parse(error.response.text),
            isValid: false,
          });
        }
      } else {
        if (!userIdParam || (userIdParam && user && user.id === Number.parseInt(userIdParam, 0))) {
          // Merge response with existing user to preserve permission fields (is_staff, is_superuser, etc.)
          // that are not returned by the profile update API endpoint
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dispatch(authAction.setUserInfo({ ...(user as any), ...(response as any) }));
          onSaveSuccess?.(response);
        }
        setOriginalImageSrc(imageSrc);
        refetchUserDetails();
        successNotification(USER_PROFILE_INFO_UPDATE);
        setIsDirty(false);
      }
    }
    setSaveButtonHasClicked(false);
    setImageHasBeenUploaded(false);
  };

  const uploadImageProfile = async () => {
    dispatch(globalAction.showLoadingProgress(true));
    // Leverage a ref to always get the latest value of the state
    const signedUrl = (await getFirmAssetSignedUrl(firmId as number, 'png')) as FirmAssetSignedUrlResponse;
    const file = await getCroppedImage(imageSrc, latestPixelCrop.current as PixelCrop);

    uploadImageFile(signedUrl.url, file as File)
      .then(response => {
        setFormState({
          ...formState,
          values: {
            ...formState.values,
            profile: {
              ...formState.values.profile,
              picture_url: (response as FirmAssetSignedUrlResponse).url.split('?')[0],
            },
          },
        });
        setImageHasBeenUploaded(true);
      })
      .catch(error => {
        errorNotification(error);
        setImageHasBeenUploaded(false);
      })
      .finally(() => {
        dispatch(globalAction.showLoadingProgress(false));
      });
  };

  const handleSaveButton = () => {
    setSaveButtonHasClicked(true);
    if (formState.isValid) {
      if (imageSrc && imageSrc !== originalImageSrc) {
        uploadImageProfile();
      } else {
        saveUserProfileData();
      }
    }
  };

  // Show Save button always; disabled until dirty and valid
  useEffect(() => {
    setPageActions?.({
      mainAction: {
        id: 'user-profile',
        label: 'Save',
        isActive: !isDirty || !formState.isValid,
        callback: handleSaveButton,
      },
    });
  }, [isDirty, formState]);

  // Clear Save button on unmount
  useEffect(
    () => () => {
      setPageActions?.(null);
    },
    []
  );

  const breadcrumbs: PageBreadcrumb[] = useMemo(() => {
    if (userInfo && (userInfo as string) !== ERROR_403) {
      return [
        {
          title: 'Account Settings',
        },
        {
          title: (userInfo as UserProfileType).email ?? '',
        },
      ];
    }

    return [];
  }, [userInfo]);

  useEffect(() => {
    setPageTitle?.('Account Settings');
    setPageBreadcrumbs?.(breadcrumbs);

    return () => {
      setPageTitle?.(null);
      setPageBreadcrumbs?.(null);
    };
  }, [breadcrumbs, setPageTitle, setPageBreadcrumbs]);

  useEffect(() => {
    if (userInfo && userInfo !== ERROR_403) {
      const userInfoValue = userInfo as UserProfileType;
      const defaultImageUrl = userInfoValue.profile?.picture_url ?? '';
      setFormState(formStateData => ({
        ...formStateData,
        values: {
          username: userInfoValue.username ?? '',
          first_name: userInfoValue.first_name ?? '',
          last_name: userInfoValue.last_name ?? '',
          email: userInfoValue.email ?? '',
          profile: {
            id: userInfoValue.profile?.id?.toString() ?? '',
            picture_url: defaultImageUrl,
            receive_feature_emails: userInfoValue.profile?.receive_feature_emails ?? false,
            receive_document_emails: userInfoValue.profile?.receive_document_emails ?? false,
          },
        },
      }));
      setImageSrc(defaultImageUrl);
      setOriginalImageSrc(defaultImageUrl);
    }
  }, [userInfo]);

  useEffect(() => {
    if (userSelected) {
      setUserInfo(userSelected as unknown as UserProfileType);
    }
  }, [userSelected]);

  const getErrors = () => validate(formState.values, schema);

  const isValidForm = () => {
    const errors = getErrors();
    return isUndefined(errors) || isEmpty(errors);
  };

  useEffect(() => {
    if (saveButtonHasClicked && imageHasBeenUploaded && formState.values.profile.picture_url !== originalImageSrc) {
      saveUserProfileData();
    }
  }, [formState.values.profile.picture_url, imageHasBeenUploaded]);

  useEffect(() => {
    setFormState(formStateData => ({
      ...formStateData,
      values: {
        ...formStateData.values,
        profile: {
          ...formStateData.values.profile,
          picture_url: imageSrc,
        },
      },
      isValid: isValidForm(),
    }));
  }, [imageSrc]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setIsDirty(true);
    const key = event.target.name;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const errors = getErrors();

    // Check if this is a profile field
    const isProfileField = key === 'receive_feature_emails' || key === 'receive_document_emails';

    setFormState(formStateData => ({
      ...formStateData,
      values: {
        ...formStateData.values,
        ...(isProfileField
          ? {
            profile: {
              ...formStateData.values.profile,
              [key]: value,
            },
          }
          : {
            [key]: value,
          }),
      },
      touched: {
        ...formStateData.touched,
        ...(isProfileField
          ? {
            profile: {
              ...formStateData.touched.profile,
              [key]: true,
            },
          }
          : {
            [key]: true,
          }),
      },
      isValid: isValidForm(),
      errors: errors ?? formStateData.errors,
    }));
  };

  const hasError = (field: keyof UserProfileFormState['errors']) =>
    formState.touched[field] && (formState.errors[field] as string[]).length > 0;

  if (isNull(userInfo)) {
    return <UserFormSkeleton />;
  }

  if (userInfo === ERROR_403) {
    return <MessageBox title={USER_ACCESS_DENIED} fullWidth={false} />;
  }

  if (isUndefined(userInfo)) {
    return <MessageBox title={USER_PROFILE_ERROR} fullWidth={false} />;
  }

  const emailNotificationOptions: EmailNotificationOption[] = [
    {
      name: 'receive_feature_emails',
      label: 'Receive email notifications of new features',
      ariaLabel: 'Receive email notifications of new features',
      checked: formState.values.profile.receive_feature_emails,
      onChange: handleChange,
    },
    {
      name: 'receive_document_emails',
      label: 'Receive email notifications of all document uploads',
      ariaLabel: 'Receive email notifications of all document uploads',
      checked: formState.values.profile.receive_document_emails,
      onChange: handleChange,
    },
  ];

  return (
    <UserProfileStyledGrid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6">Personal Data</Typography>
        <br />
        <Grid container spacing={4}>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('first_name')}
              fullWidth
              helperText={hasError('first_name') ? formState.errors.first_name[0] : null}
              label="First name"
              name="first_name"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.first_name}
              slotProps={{ input: { 'aria-label': 'first_name' } }}
              id="first_name"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('last_name')}
              fullWidth
              helperText={hasError('last_name') ? formState.errors.last_name[0] : null}
              label="Last name"
              name="last_name"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.last_name}
              slotProps={{ input: { 'aria-label': 'last_name' } }}
              id="last_name"
            />
          </Grid>
        </Grid>
        <br />
        <br />
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('email')}
              fullWidth
              helperText={hasError('email') ? formState.errors.email[0] : null}
              label="Email"
              name="email"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.email}
              slotProps={{ input: { 'aria-label': 'email' } }}
              id="email"
            />
          </Grid>
        </Grid>
        <br />
        <Button onClick={handleResetPassword} variant="outlined" color="primary" disabled={isShowLoadingProgress}>
          <EmailOutlinedIcon className={userProfileClasses.emailIconStyle} /> {SEND_PASSWORD_RESET_EMAIL}
        </Button>
        <br />
        <EmailsCheckbox options={emailNotificationOptions} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ImageContainer
          imageSrc={imageSrc}
          setImageSrc={handleSetImageSrc}
          setCroppedAreaPixels={setCroppedAreaPixels}
        />
      </Grid>
      <Grid size={12}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <ZoomControl />
          {isWorkboard && <ThemeModeControl />}
        </Box>
      </Grid>
      <Grid size={12}>
        <ConnectedApps />
      </Grid>
    </UserProfileStyledGrid>
  );
};

export default UserProfile;
